/**
 * 90-day chronotype regression card. Shows midpoint trend, social jetlag,
 * 30-day forward projection. Calm-tile UX, no alarms.
 */
import { store } from '../store.ts';
import { regressChronotype } from '../chassis/chronotype.ts';
import { formatClock } from '../chassis/ayurveda.ts';
import type { SleepNight } from '../types.ts';

const CLASS_LABEL: Record<ReturnType<typeof regressChronotype>['classification'], { hi: string; en: string }> = {
  stable: { hi: 'mauj mein, sthir', en: 'stable rhythm' },
  drifting_later: { hi: 'raat ki taraf khinch raha', en: 'midpoint shifting later' },
  drifting_earlier: { hi: 'subah ki taraf khinch raha', en: 'midpoint shifting earlier' },
  erratic: { hi: 'be-tartib', en: 'erratic, hard to pin' },
};

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
    container-type: inline-size;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background: linear-gradient(180deg, oklch(11% 0.04 270 / 0.55), oklch(8% 0.03 268 / 0.65));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 8px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(22px, 5vw, 30px);
    color: oklch(94% 0.04 88);
    letter-spacing: -0.01em;
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 14px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
    margin-top: 18px;
  }
  .stat {
    padding: 12px 14px;
    border-left: 1px solid oklch(80% 0.04 80 / 0.18);
  }
  .stat-lbl {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(64% 0.04 60);
  }
  .stat-val {
    font-family: var(--display);
    font-size: 28px;
    color: oklch(94% 0.04 88);
    font-weight: 500;
  }
  .verdict {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid oklch(80% 0.04 80 / 0.1);
    font-family: var(--display);
    font-style: italic;
    color: oklch(82% 0.05 60);
    font-size: 14px;
  }
  svg.trend {
    width: 100%;
    height: 80px;
    margin-top: 12px;
  }
  @container (min-width: 520px) {
    .grid { grid-template-columns: repeat(4, 1fr); }
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · Chronotype regression · 90d</div>
  <h3>Aapki neend ka rukh</h3>
  <div class="sub">Midpoint regression · weekend jetlag · 30-day forecast</div>

  <svg class="trend" id="trend" viewBox="0 0 320 80" preserveAspectRatio="none"></svg>

  <div class="grid">
    <div class="stat">
      <div class="stat-lbl">Avg midpoint</div>
      <div class="stat-val" id="mid">—</div>
    </div>
    <div class="stat">
      <div class="stat-lbl">Drift / week</div>
      <div class="stat-val" id="drift">—</div>
    </div>
    <div class="stat">
      <div class="stat-lbl">30-day forecast</div>
      <div class="stat-val" id="proj">—</div>
    </div>
    <div class="stat">
      <div class="stat-lbl">Social jetlag</div>
      <div class="stat-val" id="jetlag">—</div>
    </div>
  </div>
  <div class="verdict" id="verdict">—</div>
</div>
`;

export class AramgahChronoPrediction extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.nights);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(nights: readonly SleepNight[]): void {
    const r = regressChronotype(nights);
    const setText = (id: string, v: string) => {
      const el = this.#shadow.getElementById(id);
      if (el) el.textContent = v;
    };
    setText('mid', formatClock(r.midpoint_avg_h));
    setText('drift', `${r.midpoint_trend_min_per_week >= 0 ? '+' : ''}${r.midpoint_trend_min_per_week}m`);
    setText('proj', formatClock(r.midpoint_projected_30d_h));
    setText('jetlag', `${r.social_jetlag_h >= 0 ? '+' : ''}${r.social_jetlag_h}h`);
    const label = CLASS_LABEL[r.classification];
    setText('verdict', `${label.hi} · ${label.en}`);
    this.#drawTrend(nights);
  }

  #drawTrend(nights: ReadonlyArray<{ date: string; sleep_start_iso: string; sleep_end_iso: string }>): void {
    const svg = this.#shadow.getElementById('trend') as unknown as SVGElement | null;
    if (!svg) return;
    if (nights.length === 0) return;
    const sorted = [...nights].sort((a, b) => a.date.localeCompare(b.date));
    const midpoints = sorted.map((n) => {
      const s = new Date(n.sleep_start_iso);
      const e = new Date(n.sleep_end_iso);
      const sh = s.getUTCHours() + s.getUTCMinutes() / 60;
      const eh = e.getUTCHours() + e.getUTCMinutes() / 60;
      const ew = eh < sh ? eh + 24 : eh;
      const m = (sh + ew) / 2;
      return m > 24 ? m - 24 : m;
    });
    const min = Math.min(...midpoints);
    const max = Math.max(...midpoints);
    const range = max - min || 1;
    const w = 320;
    const h = 80;
    const points = midpoints.map((m, i) => {
      const x = (i / Math.max(1, midpoints.length - 1)) * w;
      const y = h - ((m - min) / range) * (h - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    svg.innerHTML = `
<defs>
  <linearGradient id="trendG" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stop-color="oklch(86% 0.14 82)" stop-opacity="0.4"/>
    <stop offset="100%" stop-color="oklch(86% 0.14 82)" stop-opacity="0"/>
  </linearGradient>
</defs>
<polyline points="${points}" fill="none" stroke="oklch(86% 0.14 82 / 0.9)" stroke-width="1.5"/>
<polygon points="0,${h} ${points} ${w},${h}" fill="url(#trendG)"/>
`;
  }
}

customElements.define('aramgah-chrono-prediction', AramgahChronoPrediction);
